import { useEffect, useRef, useState } from 'react';
import BounceLoader from 'react-spinners/BounceLoader';
import { AnimatePresence, motion } from 'framer-motion';

interface LoadingStage {
  label: string; // Label to show in completed badges
  duration: number; // ms before moving to next stage
}

const LOADING_STAGES: LoadingStage[] = [
  {
    label: 'Searching 500,000+ policy quotes',
    duration: 1800,
  },
  {
    label: 'Gender',
    duration: 900,
  },
  {
    label: 'Age',
    duration: 900,
  },
  {
    label: 'Tobacco Status',
    duration: 900,
  },
  {
    label: 'State',
    duration: 900,
  },
  {
    label: 'Calculating Return on Investment',
    duration: 1500,
  },
  {
    label: 'Finalizing',
    duration: Infinity, // Final stage - waits for data to load
  },
];

interface QuoteLoadingSequenceProps {
  isDataLoaded: boolean;
  onComplete: () => void;
}

export function QuoteLoadingSequence({ isDataLoaded, onComplete }: QuoteLoadingSequenceProps) {
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [completedStages, setCompletedStages] = useState<number[]>([]);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const hasCalledComplete = useRef(false);

  // Progress through animation stages
  useEffect(() => {
    const stage = LOADING_STAGES[currentStageIndex];
    const isFinalStage = currentStageIndex === LOADING_STAGES.length - 1;

    if (isFinalStage && !isAnimationComplete) {
      setIsAnimationComplete(true);
      setCompletedStages(prev => [...prev, currentStageIndex]);
      return;
    }

    if (stage.duration === Infinity) {
      return;
    }

    const timer = setTimeout(() => {
      setCompletedStages(prev => [...prev, currentStageIndex]);
      setCurrentStageIndex(prev => Math.min(prev + 1, LOADING_STAGES.length - 1));
    }, stage.duration);

    return () => clearTimeout(timer);
  }, [currentStageIndex, isAnimationComplete]);

  useEffect(() => {
    if (isAnimationComplete && isDataLoaded && !hasCalledComplete.current) {
      hasCalledComplete.current = true;
      setTimeout(onComplete, 2000);
    }
  }, [isAnimationComplete, isDataLoaded, onComplete]);

  // Progress includes current stage being worked on
  const progress = ((currentStageIndex + 1) / LOADING_STAGES.length) * 100;

  return (
    <div className="flex min-h-[500px] items-center justify-center py-12">
      <div className="flex w-full max-w-4xl flex-col items-center gap-8">
        {/* Static main heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
          className="flex flex-col items-center gap-3 text-center"
        >
          <h1 className="text-4xl font-bold text-gray-900">
            Searching for your best coverage options
          </h1>
          <p className="text-lg text-gray-700">
            Analyzing thousands of policies to find the most efficient coverage for you
          </p>
        </motion.div>

        {/* Completed stages checklist - displayed as badges */}
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <AnimatePresence initial={false}>
            {LOADING_STAGES.slice(0, Math.max(currentStageIndex + 1, completedStages.length)).map(
              (stage, index) => {
                const isCompleted = completedStages.includes(index);
                const isCurrent = currentStageIndex === index && !isCompleted;

                return (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      transition: {
                        type: 'spring',
                        stiffness: 500,
                        damping: 30,
                        mass: 1,
                      },
                    }}
                    exit={{ opacity: 0, scale: 0.5 }}
                    layout
                    transition={{
                      layout: {
                        type: 'spring',
                        stiffness: 500,
                        damping: 35,
                      },
                    }}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${
                      isCompleted
                        ? 'bg-primary/10 text-primary'
                        : isCurrent
                          ? 'bg-primary/5 text-primary/80'
                          : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <AnimatePresence mode="wait">
                      {isCompleted && (
                        <motion.svg
                          key="checkmark"
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{
                            opacity: 1,
                            scale: 1,
                            transition: {
                              type: 'spring',
                              stiffness: 500,
                              damping: 25,
                            },
                          }}
                          exit={{ opacity: 0, scale: 0 }}
                          className="h-4 w-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </motion.svg>
                      )}
                    </AnimatePresence>
                    {stage.label}
                  </motion.span>
                );
              },
            )}
          </AnimatePresence>
        </div>

        {/* Progress bar */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0.9 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
          className="w-full max-w-2xl"
        >
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200">
            <motion.div
              className="bg-primary h-full rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
